<template>
    <div
        class="flex flex-col rounded-xl border border-slate-200 bg-white px-4 pb-1 dark:border-slate-800 dark:bg-slate-900"
    >
        <div
            class="sticky top-0 z-10 -mx-1 mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 bg-white px-1 py-3 dark:bg-slate-900"
        >
            <div class="relative min-w-[240px] flex-1">
                <MagnifyingGlassIcon
                    class="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400"
                />
                <input
                    ref="search"
                    v-model="search"
                    type="search"
                    :placeholder="placeholder"
                    class="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-16 text-sm text-slate-700 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
                <kbd
                    v-show="!search"
                    class="pointer-events-none absolute right-3 top-2 rounded border border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-400 dark:border-slate-600"
                >
                    /
                </kbd>
            </div>

            <p class="text-sm text-slate-500 dark:text-slate-400">
                <span
                    class="font-semibold text-slate-700 dark:text-slate-200"
                    >{{ activeCount }}</span
                >
                de {{ permissions.length }} ativas
                <span v-if="search"> · {{ filtered.length }} no filtro</span>
            </p>
        </div>

        <div class="flex gap-6">
            <nav class="hidden w-52 shrink-0 xl:block">
                <ul class="sticky top-20 flex flex-col gap-0.5">
                    <li v-for="group in groups" :key="group.key">
                        <button
                            @click.prevent="openAndScrollTo(group.key)"
                            type="button"
                            class="flex w-full rounded-md px-2 py-1.5 text-left hover:bg-slate-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500 dark:hover:bg-slate-800"
                        >
                            <span
                                class="flex items-baseline justify-between gap-2"
                            >
                                <span
                                    class="truncate text-[13px] text-slate-600 dark:text-slate-300"
                                >
                                    {{ group.label }}
                                </span>
                                <span
                                    class="shrink-0 text-[11px] tabular-nums"
                                    :class="
                                        group.activeCount
                                            ? 'text-slate-600 dark:text-slate-300'
                                            : 'text-slate-400 dark:text-slate-600'
                                    "
                                >
                                    {{ group.activeCount }}/{{
                                        group.items.length
                                    }}
                                </span>
                            </span>
                        </button>
                    </li>
                </ul>
            </nav>

            <div class="min-w-0 flex-1">
                <p
                    v-if="!groups.length"
                    class="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
                >
                    Nada encontrado para "{{ search }}". Tente o nome técnico,
                    como
                    <span class="font-mono text-slate-600 dark:text-slate-300"
                        >view.orders</span
                    >.
                </p>

                <section
                    v-for="group in groups"
                    :key="group.key"
                    :ref="'group-' + group.key"
                    class="mb-6 scroll-mt-20"
                >
                    <div
                        class="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-200 pb-2 dark:border-slate-700"
                    >
                        <button
                            @click.prevent="toggleGroup(group.key)"
                            type="button"
                            :aria-expanded="isOpen(group.key)"
                            class="flex items-center gap-2 rounded text-[13px] font-semibold text-slate-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500 dark:text-slate-100"
                        >
                            <ChevronUpIcon
                                class="h-4 w-4 text-slate-400 transition motion-reduce:transition-none"
                                :class="
                                    isOpen(group.key)
                                        ? 'rotate-180 transform'
                                        : ''
                                "
                            />
                            {{ group.label }}
                        </button>

                        <span
                            class="text-xs tabular-nums text-slate-400 dark:text-slate-500"
                        >
                            {{ group.activeCount }}/{{ group.items.length }}
                        </span>

                        <span class="ml-auto flex shrink-0 gap-3 text-xs">
                            <button
                                @click.prevent="setGroup(group, true)"
                                :disabled="loading"
                                type="button"
                                class="text-slate-500 hover:text-blue-600 disabled:cursor-not-allowed dark:text-slate-400 dark:hover:text-blue-400"
                            >
                                Marcar grupo
                            </button>
                            <button
                                @click.prevent="setGroup(group, false)"
                                :disabled="loading"
                                type="button"
                                class="text-slate-500 hover:text-blue-600 disabled:cursor-not-allowed dark:text-slate-400 dark:hover:text-blue-400"
                            >
                                Limpar
                            </button>
                        </span>
                    </div>

                    <ul v-show="isOpen(group.key)" class="mt-1">
                        <li
                            v-for="permission in group.items"
                            :key="permission.id"
                            class="border-l-2 transition-colors motion-reduce:transition-none"
                            :class="rowClass(permission)"
                        >
                            <label
                                class="relative flex cursor-pointer items-center gap-3 rounded-r py-2 pl-3 pr-2 hover:bg-slate-100/70 dark:hover:bg-white/5"
                            >
                                <input
                                    v-model="permission.has"
                                    type="checkbox"
                                    :disabled="loading"
                                    class="peer sr-only"
                                />
                                <span
                                    class="relative h-5 w-9 shrink-0 rounded-full border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-blue-400 motion-reduce:transition-none"
                                    :class="
                                        permission.has
                                            ? 'border-blue-600 bg-blue-600'
                                            : 'border-slate-300 bg-slate-200 dark:border-slate-600 dark:bg-slate-700'
                                    "
                                >
                                    <span
                                        class="absolute left-[2px] top-[2px] h-4 w-4 rounded-full bg-white shadow transition-transform motion-reduce:transition-none"
                                        :class="
                                            permission.has
                                                ? 'translate-x-4'
                                                : 'translate-x-0'
                                        "
                                    ></span>
                                </span>

                                <span class="min-w-0 flex-1">
                                    <span
                                        class="block text-sm text-slate-700 dark:text-slate-200"
                                    >
                                        {{ permission.title }}
                                    </span>
                                    <span
                                        class="block truncate font-mono text-[11px] text-slate-400 dark:text-slate-500"
                                    >
                                        {{ permission.name }}
                                    </span>
                                </span>

                                <span
                                    v-if="permission.restricted"
                                    class="shrink-0 rounded border border-amber-400/60 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400"
                                >
                                    restrita
                                </span>

                                <span
                                    v-if="changeOf(permission)"
                                    class="h-2 w-2 shrink-0 rounded-full"
                                    :class="
                                        changeOf(permission) === 'granted'
                                            ? 'bg-emerald-500'
                                            : 'bg-rose-500'
                                    "
                                    :title="
                                        changeOf(permission) === 'granted'
                                            ? 'Será concedida ao salvar'
                                            : 'Será revogada ao salvar'
                                    "
                                ></span>
                            </label>
                        </li>
                    </ul>
                </section>
            </div>
        </div>

        <div
            v-if="hasChanges"
            class="sticky bottom-0 z-10 mt-2 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white py-3 dark:border-slate-700 dark:bg-slate-900"
        >
            <p class="text-sm text-slate-600 dark:text-slate-300">
                <span
                    v-if="changes.granted.length"
                    class="font-semibold text-emerald-600 dark:text-emerald-400"
                >
                    +{{ changes.granted.length }}
                </span>
                <span
                    v-if="changes.revoked.length"
                    class="font-semibold text-rose-600 dark:text-rose-400"
                >
                    −{{ changes.revoked.length }}
                </span>
                <span class="ml-1">{{ changeSummary }}</span>
            </p>

            <input
                :value="reason"
                @input="$emit('update:reason', $event.target.value)"
                type="text"
                maxlength="500"
                placeholder="Motivo (opcional)"
                class="min-w-[200px] flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />

            <button
                @click.prevent="discard"
                :disabled="loading"
                type="button"
                class="rounded-lg px-3 py-2 text-sm text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed dark:text-slate-400 dark:hover:text-slate-200"
            >
                Descartar
            </button>
            <button
                @click.prevent="$emit('save')"
                :disabled="loading"
                type="button"
                class="inline-flex items-center gap-2 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600 disabled:cursor-not-allowed motion-reduce:transition-none"
            >
                <svg
                    v-if="loading"
                    class="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
                Salvar alterações
            </button>
        </div>
    </div>
</template>

<script>
import { ChevronUpIcon } from "@heroicons/vue/20/solid";
import { MagnifyingGlassIcon } from "@heroicons/vue/24/outline";

export default {
    components: { ChevronUpIcon, MagnifyingGlassIcon },

    props: {
        permissions: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "Buscar permissão...",
        },
        reason: {
            type: String,
            default: "",
        },
        dirty: {
            type: Boolean,
            default: false,
        },
        dirtyLabel: {
            type: String,
            default: "",
        },
    },

    emits: ["save", "update:reason"],

    data() {
        return {
            search: "",
            openGroups: [],
            closedGroups: [],
            snapshot: {},
        };
    },

    computed: {
        activeCount() {
            return this.permissions.filter((permission) => permission.has)
                .length;
        },

        filtered() {
            const term = this.normalize(this.search);

            if (!term) {
                return this.permissions;
            }

            return this.permissions.filter((permission) => {
                return (
                    this.normalize(permission.title).includes(term) ||
                    this.normalize(permission.name).includes(term) ||
                    this.normalize(permission.group_label).includes(term)
                );
            });
        },

        groups() {
            const groups = [];
            const index = {};

            this.filtered.forEach((permission) => {
                const key = permission.group || "outras";

                if (index[key] === undefined) {
                    index[key] = groups.length;
                    groups.push({
                        key,
                        label: permission.group_label || "Outras",
                        items: [],
                        activeCount: 0,
                    });
                }

                const group = groups[index[key]];
                group.items.push(permission);

                if (permission.has) {
                    group.activeCount += 1;
                }
            });

            return groups;
        },

        autoOpenGroups() {
            const keys = [];

            this.permissions.forEach((permission) => {
                const key = permission.group || "outras";

                if (this.snapshot[permission.id] && !keys.includes(key)) {
                    keys.push(key);
                }
            });

            return keys;
        },

        changes() {
            const granted = [];
            const revoked = [];

            this.permissions.forEach((permission) => {
                const change = this.changeOf(permission);

                if (change === "granted") {
                    granted.push(permission);
                }

                if (change === "revoked") {
                    revoked.push(permission);
                }
            });

            return { granted, revoked };
        },

        hasChanges() {
            return (
                this.dirty ||
                this.changes.granted.length > 0 ||
                this.changes.revoked.length > 0
            );
        },

        changeSummary() {
            const names = [...this.changes.granted, ...this.changes.revoked]
                .slice(0, 3)
                .map((permission) => permission.title);

            if (!names.length) {
                return this.dirtyLabel;
            }

            const rest =
                this.changes.granted.length +
                this.changes.revoked.length -
                names.length;

            return rest > 0
                ? `${names.join(", ")} e mais ${rest}`
                : names.join(", ");
        },
    },

    watch: {
        permissions: {
            immediate: true,
            handler() {
                this.commit();
            },
        },
    },

    mounted() {
        window.addEventListener("keydown", this.focusSearch);
    },

    beforeUnmount() {
        window.removeEventListener("keydown", this.focusSearch);
    },

    methods: {
        /**
         * Guarda o estado salvo para comparar com o que o operador mexeu.
         */
        commit() {
            const snapshot = {};

            this.permissions.forEach((permission) => {
                snapshot[permission.id] = permission.has;
            });

            const openBeforeCommit = this.groups
                .map((group) => group.key)
                .filter((key) => this.isOpen(key));

            this.snapshot = snapshot;
            this.openGroups = [
                ...new Set([...this.openGroups, ...openBeforeCommit]),
            ].filter((key) => !this.closedGroups.includes(key));
        },

        discard() {
            this.permissions.forEach((permission) => {
                permission.has = this.snapshot[permission.id] ?? permission.has;
            });
        },

        changeOf(permission) {
            const saved = this.snapshot[permission.id];

            if (saved === undefined || saved === permission.has) {
                return null;
            }

            return permission.has ? "granted" : "revoked";
        },

        rowClass(permission) {
            if (permission.has && permission.restricted) {
                return "border-amber-400 bg-amber-50 dark:bg-amber-500/10";
            }

            if (permission.has) {
                return "border-blue-500 bg-slate-50 dark:bg-white/5";
            }

            if (permission.restricted) {
                return "border-amber-300/60 dark:border-amber-500/30";
            }

            return "border-transparent";
        },

        normalize(value) {
            return (value || "")
                .toString()
                .toLowerCase()
                .normalize("NFD")
                .replace(/[̀-ͯ]/g, "");
        },

        /**
         * Grupos com alguma permissão ativa abrem sozinhos.
         */
        isOpen(key) {
            if (this.search !== "" || this.openGroups.includes(key)) {
                return true;
            }

            if (this.closedGroups.includes(key)) {
                return false;
            }

            return this.autoOpenGroups.includes(key);
        },

        toggleGroup(key) {
            if (this.isOpen(key)) {
                this.openGroups = this.openGroups.filter(
                    (item) => item !== key,
                );
                this.closedGroups = [...this.closedGroups, key];

                return;
            }

            this.closedGroups = this.closedGroups.filter(
                (item) => item !== key,
            );
            this.openGroups = [...this.openGroups, key];
        },

        openAndScrollTo(key) {
            this.closedGroups = this.closedGroups.filter(
                (item) => item !== key,
            );
            this.openGroups = [...this.openGroups, key];

            this.$nextTick(() => {
                const section = this.$refs["group-" + key];
                const element = Array.isArray(section) ? section[0] : section;

                element?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        },

        setGroup(group, value) {
            group.items.forEach((permission) => (permission.has = value));
        },

        focusSearch(event) {
            if (event.key !== "/" || this.isTyping(event.target)) {
                return;
            }

            event.preventDefault();
            this.$refs.search?.focus();
        },

        isTyping(target) {
            return ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName);
        },
    },
};
</script>
